import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import { useSubmitPrescriptionMutation } from '../../store/siteContentApi';
import { UI, DISPLAY, EASE } from '../../styles/tl5';

// Collects the one thing a prescription upload cannot work without: a
// number to ring back. The file alone is not a lead — before this existed
// the card said "Prescription received ✓" and sent nothing (API-NOTES 2.1).
//
// The caller owns the file picker and hands the chosen File in, because on
// the home band the whole card is the picker's hit area.

const MAX_MB = 8;

const prettyBytes = (n) => (n < 1024 * 1024
  ? `${Math.max(1, Math.round(n / 1024))} KB`
  : `${(n / (1024 * 1024)).toFixed(1)} MB`);

// Matches the server's rule, so the dialog rejects before a round trip.
const tenDigits = (raw) => {
  const d = String(raw || '').replace(/\D/g, '');
  const ten = d.length > 10 ? d.slice(-10) : d;
  return /^[6-9]\d{9}$/.test(ten) ? ten : '';
};

const label = { fontFamily: UI, fontSize: 13.5, fontWeight: 700, color: 'var(--tl-g9)' };
const field = {
  width: '100%',
  font: 'inherit',
  fontFamily: UI,
  fontSize: 15,
  color: 'var(--tl-g9)',
  p: '12px 14px',
  borderRadius: '14px',
  border: '1.5px solid var(--tl-line)',
  bgcolor: '#FFFFFF',
  outline: 'none',
  '&:focus': { borderColor: 'var(--tl-teal)' },
};

const PrescriptionUploadDialog = ({ open, file, source = 'home', onClose, onDone }) => {
  const [submit, { isLoading }] = useSubmitPrescriptionMutation();
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);

  // A fresh dialog each time it opens — a previous success or error must
  // not greet the next person who picks a file.
  useEffect(() => {
    if (open) { setError(''); setDone(null); }
  }, [open]);

  const tooBig = file && file.size > MAX_MB * 1024 * 1024;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!file) { setError('Pick a photo or PDF of the prescription first.'); return; }
    if (tooBig) { setError(`That file is ${prettyBytes(file.size)}. Keep it under ${MAX_MB} MB.`); return; }
    const ten = tenDigits(phone);
    if (!ten) { setError('Enter a valid 10-digit mobile number.'); return; }

    const fd = new FormData();
    fd.append('phone', ten);
    if (name.trim()) fd.append('name', name.trim());
    if (notes.trim()) fd.append('notes', notes.trim());
    fd.append('source', source);
    fd.append('files', file, file.name);

    try {
      const res = await submit(fd).unwrap();
      setDone(res);
      onDone?.(res);
    } catch (err) {
      setError(err?.data?.message || 'Could not send that. Please try again, or call us.');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={isLoading ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      // PaperProps, not slotProps.paper — MUI 5.18 silently ignores the latter.
      PaperProps={{ sx: { borderRadius: '24px', m: 2 } }}
    >
      <Box sx={{ p: 'clamp(20px, 5vw, 28px)' }}>
        {done ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box
              component="span"
              sx={{
                display: 'grid', placeItems: 'center', width: 46, height: 46,
                borderRadius: '50%', bgcolor: 'var(--tl-mint)', color: 'var(--tl-teal-d)',
                fontSize: 24, fontWeight: 800,
              }}
            >
              ✓
            </Box>
            <Typography sx={{ m: 0, fontFamily: DISPLAY, fontWeight: 700, fontSize: 22, color: 'var(--tl-g9)' }}>
              {done.files_received ? 'Prescription received' : 'We have your number'}
            </Typography>
            <Typography sx={{ m: 0, fontFamily: UI, fontSize: 15, lineHeight: 1.6, color: 'var(--tl-muted)' }}>
              {/* Never claim a file the server told us it did not store. */}
              {done.files_received
                ? 'Our team will call you within 15 minutes to confirm the tests and book a slot.'
                : 'The file did not upload, but we have your number and will call you within 15 minutes — please keep the prescription handy.'}
            </Typography>
            <Box
              component="span"
              sx={{
                alignSelf: 'flex-start', mt: 0.5, px: 1.5, py: 0.75, borderRadius: '999px',
                bgcolor: 'var(--tl-mint)', color: 'var(--tl-teal-d)',
                fontFamily: UI, fontSize: 13, fontWeight: 800,
              }}
            >
              Reference {done.request_id}
            </Box>
            <Box
              component="button"
              type="button"
              onClick={onClose}
              sx={{
                mt: 1.5, p: '13px 18px', borderRadius: '999px', border: 0, cursor: 'pointer',
                bgcolor: 'var(--tl-g9)', color: '#FFFFFF',
                fontFamily: UI, fontSize: 15, fontWeight: 800,
                transition: `background .3s ${EASE}`,
                '&:hover': { bgcolor: 'var(--tl-g8)' },
              }}
            >
              Done
            </Box>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
            <Typography sx={{ m: 0, fontFamily: DISPLAY, fontWeight: 700, fontSize: 22, color: 'var(--tl-g9)' }}>
              Where should we call you?
            </Typography>
            <Typography sx={{ m: 0, fontFamily: UI, fontSize: 14.5, lineHeight: 1.55, color: 'var(--tl-muted)' }}>
              We read the prescription and call you back within 15 minutes to confirm the tests.
            </Typography>

            {file && (
              <Box
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.25, p: '12px 14px',
                  borderRadius: '14px', bgcolor: 'var(--tl-mint)', minWidth: 0,
                }}
              >
                <Box component="span" sx={{ fontSize: 18, flexShrink: 0 }}>📄</Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography noWrap sx={{ m: 0, fontFamily: UI, fontSize: 14, fontWeight: 700, color: 'var(--tl-g9)' }}>
                    {file.name}
                  </Typography>
                  <Typography sx={{ m: 0, fontFamily: UI, fontSize: 12.5, color: tooBig ? '#B42318' : 'var(--tl-muted)' }}>
                    {prettyBytes(file.size)}{tooBig ? ` · over the ${MAX_MB} MB limit` : ''}
                  </Typography>
                </Box>
              </Box>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <Box component="label" htmlFor="rx-phone" sx={label}>Mobile number *</Box>
              <Box
                component="input"
                id="rx-phone"
                type="tel"
                inputMode="numeric"
                autoComplete="tel"
                placeholder="+91 XXXXXXXXXX"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                sx={field}
              />
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <Box component="label" htmlFor="rx-name" sx={label}>Your name (optional)</Box>
              <Box
                component="input"
                id="rx-name"
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                sx={field}
              />
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <Box component="label" htmlFor="rx-notes" sx={label}>Anything we should know (optional)</Box>
              <Box
                component="textarea"
                id="rx-notes"
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                sx={{ ...field, resize: 'vertical' }}
              />
            </Box>

            {error && (
              <Typography role="alert" sx={{ m: 0, fontFamily: UI, fontSize: 13.5, color: '#B42318' }}>
                {error}
              </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 1.25, mt: 0.5 }}>
              <Box
                component="button"
                type="button"
                onClick={onClose}
                disabled={isLoading}
                sx={{
                  p: '13px 18px', borderRadius: '999px', cursor: 'pointer',
                  border: '1.5px solid var(--tl-line)', bgcolor: '#FFFFFF', color: 'var(--tl-g9)',
                  fontFamily: UI, fontSize: 15, fontWeight: 800,
                }}
              >
                Cancel
              </Box>
              <Box
                component="button"
                type="submit"
                disabled={isLoading}
                sx={{
                  flex: 1, p: '13px 18px', borderRadius: '999px', border: 0,
                  cursor: isLoading ? 'default' : 'pointer',
                  bgcolor: 'var(--tl-yellow)', color: 'var(--tl-g9)',
                  fontFamily: UI, fontSize: 15, fontWeight: 800,
                  opacity: isLoading ? 0.7 : 1,
                  transition: `opacity .3s ${EASE}`,
                }}
              >
                {isLoading ? 'Sending…' : 'Send prescription'}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default PrescriptionUploadDialog;
